import React from "react";
import "@/App.css";
import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import Dispatch from "@/pages/Dispatch";
import Intake from "@/pages/Intake";
import Technician from "@/pages/Technician";
import Equipment from "@/pages/Equipment";
import EquipmentDetail from "@/pages/EquipmentDetail";
import ExportPage from "@/pages/Export";
import Dashboard from "@/pages/Dashboard";
import UsersAccess from "@/pages/UsersAccess";
import Customers from "@/pages/Customers";
import CustomerDetail from "@/pages/CustomerDetail";
import ImportData from "@/pages/ImportData";
import EquipmentTypes from "@/pages/EquipmentTypes";
import SlaSettings from "@/pages/SlaSettings";
import Backup from "@/pages/Backup";
import Completed from "@/pages/Completed";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center mono text-[12px]" style={{ color: "#5F6B7A" }}>Loading…</div>;
  if (!user) return <Navigate to="/" replace />;
  return <Layout>{children}</Layout>;
}

function RoleGate({ allowed, redirectTo = "/home", children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/" replace />;
  if (!allowed.includes(user.role)) return <Navigate to={redirectTo} replace />;
  return <Layout>{children}</Layout>;
}

function RoleHome() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/" replace />;
  const home = {
    technician: "/tech",
    service_coordinator: "/dispatch",
    manager: "/dispatch",
  }[user.role] || "/tech";
  return <Navigate to={home} replace />;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Login />} />
      <Route path="/home" element={<RoleHome />} />
      <Route path="/dispatch" element={<RoleGate allowed={["manager", "service_coordinator"]}><Dispatch /></RoleGate>} />
      <Route path="/completed" element={<RoleGate allowed={["manager", "service_coordinator"]}><Completed /></RoleGate>} />
      <Route path="/dashboard" element={<RoleGate allowed={["manager", "service_coordinator"]}><Dashboard /></RoleGate>} />
      <Route path="/intake" element={<RoleGate allowed={["manager", "service_coordinator"]}><Intake /></RoleGate>} />
      <Route path="/tech" element={<RoleGate allowed={["technician"]}><Technician /></RoleGate>} />
      <Route path="/customers" element={<RoleGate allowed={["manager", "service_coordinator"]}><Customers /></RoleGate>} />
      <Route path="/customers/:id" element={<RoleGate allowed={["manager", "service_coordinator"]}><CustomerDetail /></RoleGate>} />
      <Route path="/import" element={<RoleGate allowed={["manager"]}><ImportData /></RoleGate>} />
      <Route path="/settings/equipment-types" element={<RoleGate allowed={["manager"]}><EquipmentTypes /></RoleGate>} />
      <Route path="/settings/sla" element={<RoleGate allowed={["manager"]}><SlaSettings /></RoleGate>} />
      <Route path="/settings/backup" element={<RoleGate allowed={["manager"]}><Backup /></RoleGate>} />
      <Route path="/equipment" element={<RoleGate allowed={["manager", "service_coordinator"]} redirectTo="/tech"><Equipment /></RoleGate>} />
      <Route path="/equipment/:id" element={<RoleGate allowed={["manager", "service_coordinator"]} redirectTo="/tech"><EquipmentDetail /></RoleGate>} />
      <Route path="/export" element={<RoleGate allowed={["manager"]}><ExportPage /></RoleGate>} />
      <Route path="/users" element={<RoleGate allowed={["manager"]}><UsersAccess /></RoleGate>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
      </AuthProvider>
    </BrowserRouter>
  );
}
